import React from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { checkAndUpdateOrderProgress } from "../../utils/firebaseUtils";

const fields = [
  { name: "yourName", label: "Your Name*" },
  {
    name: "chosenMissionArtOption",
    label: "Chosen Mission Art Option: Large or Small*",
  },
  { name: "missionaryName", label: "Name of Missionary*" },
  { name: "missionName", label: "Name of Mission where served*" },
  { name: "missionPresident", label: "Name of Mission President and his wife" },
  {
    name: "secondMissionPresident",
    label: "Name of 2nd Mission president and his wife, if applicable",
  },
  {
    name: "missionaryServiceDates",
    label: "Dates when the missionary served*",
  },
  {
    name: "missionPresidentServiceDates",
    label: "Dates when the Mission President served",
  },
  {
    name: "secondMissionPresidentServiceDates",
    label: "Dates when the 2nd Mission president served, if applicable",
  },
  { name: "favoriteScripture", label: "A favorite scripture or two" },
  {
    name: "flags",
    label: "Name of country/countries whose flags you want shown",
  },
  {
    name: "locationsOnMap",
    label:
      "Names of cities/states/countries you want identified on your mission map",
  },
  ...Array.from({ length: 18 }, (_, i) => [
    {
      name: `companion_${i + 1}_name`,
      label: `Name of ${i + 1}${
        i + 1 === 1 ? "st" : i + 1 === 2 ? "nd" : i + 1 === 3 ? "rd" : "th"
      } companion${i >= 10 ? ", if applicable" : ""}`,
    },
    {
      name: `companion_${i + 1}_info`,
      label: `Info about ${i + 1}${
        i + 1 === 1 ? "st" : i + 1 === 2 ? "nd" : i + 1 === 3 ? "rd" : "th"
      } companion`,
    },
  ]).flat(),
];

const MissionaryForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [formData, setFormData] = React.useState(() => {
    const initialData: { [key: string]: string } = {};
    fields.forEach((field) => {
      initialData[field.name] = "";
    });
    return initialData;
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitStatus, setSubmitStatus] = React.useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orderId) {
      setSubmitStatus({
        type: "error",
        message:
          "No order ID provided. Please access this form from your order page.",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const dataToSubmit = {
        ...formData,
        submittedAt: new Date().toISOString(),
      };

      // BLOCKED: Save form data to Firestore
      console.log(
        "BLOCKED FIRESTORE WRITE: Would save Missionary form data for order:",
        orderId
      );
      console.log("Data that would be written:", dataToSubmit);
      // const orderRef = doc(db, "orders", orderId);
      // await updateDoc(orderRef, {
      //   "formData.Missionary": dataToSubmit,
      //   "formStatus.Missionary": true,
      //   lastFormUpdate: serverTimestamp(),
      // });

      // BLOCKED: Check if this triggers a status update
      console.log(
        "BLOCKED: Would check and update order progress for order:",
        orderId
      );
      // const updatedFormStatus = { Missionary: true };
      // await checkAndUpdateOrderProgress(orderId, updatedFormStatus);

      setSubmitStatus({
        type: "success",
        message:
          "Missionary information saved successfully! Your order status has been updated.",
      });

      console.log("Form Data Submitted:", dataToSubmit);
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus({
        type: "error",
        message: "Failed to save form data. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{ maxWidth: "900px", margin: "auto", mt: 4, mb: 4 }}
      component="form"
      onSubmit={handleSubmit}
    >
      <Typography variant="h4" gutterBottom>
        Missionary Photo Art
      </Typography>
      <Typography variant="body1" paragraph>
        In order to create your customized Missionary Photo Art, we need the
        following information. Fill in the appropriate fields relative to your
        chosen layout.
      </Typography>
      <Grid container spacing={2}>
        {fields.map((field, index) => (
          <Grid size={{ xs: 12 }} key={index}>
            <TextField
              fullWidth
              name={field.name}
              label={field.label}
              variant="outlined"
              value={formData[field.name]}
              onChange={handleInputChange}
            />
          </Grid>
        ))}
        <Grid size={{ xs: 12 }}>
          {submitStatus.type && (
            <Alert severity={submitStatus.type} sx={{ mb: 2 }}>
              {submitStatus.message}
            </Alert>
          )}

          <Button
            variant="contained"
            type="submit"
            fullWidth
            disabled={isSubmitting || !orderId}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? "Saving..." : "Submit Missionary Information"}
          </Button>

          {!orderId && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please access this form from your order page to ensure proper data
              saving.
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MissionaryForm;
